import { createSelector } from 'reselect';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { ESTIMATION_FIELD_ID_ORIGINAL_ESTIMATE } from '@atlassian/jira-software-report-charting-common/src/common/constants.tsx';
import {
	ESTIMATION_STORY_POINTS,
	ESTIMATION_ISSUE_COUNT,
	ESTIMATION_ORIGINAL_ESTIMATE,
	type EstimationStatistic,
} from '@atlassian/jira-software-report-charting-common/src/common/utils/format-estimate/index.tsx';
import type { State } from '../../types';
import { getSprints } from '../sprints';
import messages from './messages';

export type MessageType = MessageDescriptor;

export const isOriginalEstimateByState = (state: State) =>
	state.entities.estimationConfig.current.customFieldId === ESTIMATION_FIELD_ID_ORIGINAL_ESTIMATE;

export const MAXIMUM_SPRINTS_DISPLAYED = 12;

export const getRecentEstimatedData = createSelector(
	[getSprints, isOriginalEstimateByState],
	(sprints, isOriginalEstimate) =>
		sprints.slice(-MAXIMUM_SPRINTS_DISPLAYED).map((sprint, index) => ({
			x: index + 1,
			y: isOriginalEstimate ? sprint.estimated / 3600 : sprint.estimated,
		})),
);

export const getRecentCompletedData = createSelector(
	[getSprints, isOriginalEstimateByState],
	(sprints, isOriginalEstimate) =>
		sprints.slice(-MAXIMUM_SPRINTS_DISPLAYED).map((sprint, index) => ({
			x: index + 1,
			y: isOriginalEstimate ? sprint.completed / 3600 : sprint.completed,
		})),
);

export const getRecentSprintAxisLabels = createSelector([getSprints], (sprints) =>
	sprints.map((sprint) => sprint.name).slice(-MAXIMUM_SPRINTS_DISPLAYED),
);

export const getCurrentEstimation = (state: State): string | null =>
	state.entities.estimationConfig.current.name;

export const getCurrentEstimationStatistic = (state: State): EstimationStatistic => {
	const estimationFieldId = state.entities.estimationConfig.current.customFieldId;
	if (estimationFieldId === null) {
		// issue count's customFieldId is null
		return ESTIMATION_ISSUE_COUNT;
	}
	if (estimationFieldId === ESTIMATION_FIELD_ID_ORIGINAL_ESTIMATE) {
		return ESTIMATION_ORIGINAL_ESTIMATE;
	}

	if (estimationFieldId.startsWith('customfield_')) {
		// story points's customFieldId is customfield_<number>
		return ESTIMATION_STORY_POINTS;
	}

	// if it's something else, fallback to issue count
	return ESTIMATION_ISSUE_COUNT;
};

const estimationToYAxisLabelMap = {
	[ESTIMATION_ISSUE_COUNT]: messages.issueCount,
	[ESTIMATION_STORY_POINTS]: messages.storyPoint,
	[ESTIMATION_ORIGINAL_ESTIMATE]: messages.originalEstimate,
} as const;

export const getYAxisLabel = (state: State): MessageDescriptor => {
	const estimationStatistic = getCurrentEstimationStatistic(state);
	if (!estimationToYAxisLabelMap[estimationStatistic]) {
		// fallback to issue count as the original logic in view
		return estimationToYAxisLabelMap[ESTIMATION_ISSUE_COUNT];
	}
	return estimationToYAxisLabelMap[estimationStatistic];
};
